import React, { useEffect, useState } from 'react';
import { Spin } from 'antd';

/**
 * Full-content-area loading state shown while a lazy page chunk is downloading.
 *
 * Features:
 *  - Thin animated progress bar fixed at the very top of the viewport
 *    (NProgress-style — users immediately see "something is happening")
 *  - Centred spinner + label in the content well so the area isn't blank
 *
 * Mount it as the <Suspense fallback> *inside* each layout's content area
 * so the navbar / sidebar stay visible during navigation.
 */
const PageLoader: React.FC = () => {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    // Animate: 0 → 30 → 65 → 85 — gives the feel of real progress
    const t1 = setTimeout(() => setWidth(30), 60);
    const t2 = setTimeout(() => setWidth(65), 300);
    const t3 = setTimeout(() => setWidth(85), 800);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);

  return (
    <>
      {/* ── Top progress bar ─────────────────────────────────────────── */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          zIndex: 9999,
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${width}%`,
            background: 'linear-gradient(90deg, #56B0FF 0%, #93d3ff 100%)',
            borderRadius: '0 2px 2px 0',
            boxShadow: '0 0 10px rgba(86,176,255,0.6)',
            transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        />
      </div>

      {/* ── Content-area spinner ─────────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          gap: 14,
        }}
      >
        <Spin size="large" />
        <span style={{ color: '#94a3b8', fontSize: 13, letterSpacing: 0.3 }}>
          Loading…
        </span>
      </div>
    </>
  );
};

export default PageLoader;
